import { useId } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface LimitFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  currency: string;
  /** The reason this value is refused, shown beside the field (spec 073 FR-018). */
  error: string | null;
  disabled?: boolean;
  autoFocus?: boolean;
}

/**
 * One amount, entered (spec 073 FR-017, FR-018).
 *
 * Shared by the default and by every override, so a limit is typed the same way
 * wherever it is typed — and, more to the point, refused the same way.
 *
 * The currency sits inside the field rather than beside it as a suffix, because it is
 * not a unit the reader chooses: there is exactly one currency (`073` FR-032), and a
 * control that looked selectable would say otherwise.
 *
 * `type="text"`, not `type="number"`. A number input silently accepts `1e5` and `-2`,
 * hands `''` back for anything it cannot parse — losing what was typed, so the message
 * beside the field cannot quote it — and mutates the value on a stray scroll wheel.
 * What is wanted here is a string, validated against the contract's own pattern.
 */
export function LimitField({
  label,
  value,
  onChange,
  currency,
  error,
  disabled = false,
  autoFocus = false,
}: LimitFieldProps) {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm"
        >
          {currency === 'USD' ? '$' : currency}
        </span>
        <Input
          id={id}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          disabled={disabled}
          autoFocus={autoFocus}
          inputMode="decimal"
          // Announced rather than only coloured: the reason is the thing that tells
          // someone what to type instead, and a red border does not say it.
          aria-invalid={error !== null}
          aria-describedby={error === null ? undefined : errorId}
          className={`ps-7 tabular-nums ${currency === 'USD' ? '' : 'ps-14'}`}
          placeholder="0.00"
        />
      </div>
      {error === null ? null : (
        <p id={errorId} role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
